/**
 * ExportChart — high-fidelity variant of ForecastChart tuned for PDF capture.
 *
 * Used off-screen during weeklyPdfExport.js. Key differences vs. the live
 * chart:
 *   - Larger axis tick fonts (16px vs 11px) for legibility after rasterisation.
 *   - Heavier line weights and bigger ₹/MWh label.
 *   - No animation, no tooltip cursor (just a static, print-ready picture).
 *   - Fixed 1100×440 logical size — the export harness renders into that box.
 */
import { useMemo } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer,
} from "recharts";

const ACTUAL = "#FFFFFF";
const FORECAST = "#CCFF00";
const GRID = "rgba(255,255,255,0.08)";
const AXIS = "rgba(255,255,255,0.55)";

export default function ExportChart({ data, height = 400 }) {
  const prepared = useMemo(
    () => data.map((b) => ({
      ...b,
      bandLow: b.p10,
      bandSpan: Math.max(0, b.p90 - b.p10),
    })),
    [data]
  );

  const xTicks = useMemo(
    () => prepared.filter((_, i) => i % 8 === 0).map((d) => d.t),
    [prepared]
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={prepared}
        margin={{ top: 20, right: 32, bottom: 14, left: 24 }}
      >
        <defs>
          <linearGradient id="exportBand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.28} />
            <stop offset="100%" stopColor="#CCFF00" stopOpacity={0.05} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke={GRID} strokeDasharray="2 4" vertical={false} />

        <XAxis
          dataKey="t"
          ticks={xTicks}
          tick={{ fill: AXIS, fontSize: 15, fontFamily: "Inter, system-ui, sans-serif", letterSpacing: "0.04em" }}
          stroke={GRID}
          tickLine={false}
          axisLine={{ stroke: GRID, strokeWidth: 1 }}
          tickMargin={8}
        />
        <YAxis
          tick={{ fill: AXIS, fontSize: 15, fontFamily: "Inter, system-ui, sans-serif" }}
          stroke={GRID}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
          domain={[0, 'dataMax + 500']}
          tickCount={6}
          width={50}
          label={{
            value: "Rs / MWh",
            angle: -90, position: "insideLeft", offset: 8,
            style: { fill: AXIS, fontSize: 13, fontFamily: "Inter, sans-serif", letterSpacing: "0.08em", textAnchor: "middle" },
          }}
        />

        {/* ₹10k cap line */}
        <ReferenceLine
          y={10000}
          stroke="rgba(255,184,0,0.55)"
          strokeDasharray="6 6"
          strokeWidth={1.2}
          label={{ value: "Rs 10k cap", fill: "#FFB800", fontSize: 12, position: "insideTopRight", fontFamily: "Inter, sans-serif" }}
        />

        {/* P10-P90 band */}
        <Area
          type="monotone" dataKey="bandLow"
          stackId="band" stroke="none" fill="transparent" isAnimationActive={false}
        />
        <Area
          type="monotone" dataKey="bandSpan"
          stackId="band" stroke="rgba(204,255,0,0.55)" strokeWidth={1.2}
          fill="url(#exportBand)" isAnimationActive={false}
        />

        {/* Actual IEX */}
        <Line
          type="monotone" dataKey="actual"
          stroke={ACTUAL} strokeWidth={2.4}
          dot={false} isAnimationActive={false}
        />

        {/* PB p50 forecast (lime, dashed) */}
        <Line
          type="monotone" dataKey="p50"
          stroke={FORECAST} strokeWidth={2.8}
          strokeDasharray="6 4" dot={false} isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
